"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { PizzaType } from "@/api/custom-schema";
import { getPhotoQualityCheckOptions } from "./photo-quality-checks";
import { useLocalization } from "@/context/localization-context";
import { formatDate } from "@/lib/date-format";

interface ActiveFiltersProps {
  idFilter: string;
  setIdFilter: (value: string) => void;
  nameFilter: string;
  setNameFilter: (value: string) => void;
  orderFilter: string;
  setOrderFilter: (value: string) => void;
  captureAtFilterStart?: Date;
  captureAtFilterEnd?: Date;
  setCaptureAtFilterStart: (value?: Date) => void;
  setCaptureAtFilterEnd: (value?: Date) => void;
  classificationFilter: string;
  setClassificationFilter: (value: string) => void;
  confidenceFilter: string;
  setConfidenceFilter: (value: string) => void;
  captureCountFilter: string;
  setCaptureCountFilter: (value: string) => void;
  componentCountFilter: string;
  setComponentCountFilter: (value: string) => void;
  donenessFilter: string;
  setDonenessFilter: (value: string) => void;
  overallEvaluationStatusFilter: string[];
  setOverallEvaluationStatusFilter: (value: string[]) => void;
  baseComponentsStatusFilter: string[];
  setBaseComponentsStatusFilter: (value: string[]) => void;
  extrasStatusFilter: string[];
  setExtrasStatusFilter: (value: string[]) => void;
  removalsStatusFilter: string[];
  setRemovalsStatusFilter: (value: string[]) => void;
  pizzaSaverStatusFilter: string[];
  setPizzaSaverStatusFilter: (value: string[]) => void;
  pizzaTypeFilter: string[];
  setPizzaTypeFilter: (value: string[]) => void;
  pizzaTypes?: PizzaType[];
  onFilterClick?: () => void;
  hasExtrasFilter: boolean;
  setHasExtrasFilter: (value: boolean) => void;
  hasRemovalsFilter: boolean;
  setHasRemovalsFilter: (value: boolean) => void;
  photoQualityStatusFilter: string[];
  setPhotoQualityStatusFilter: (value: string[]) => void;
  photoQualityCheckFilter: string[];
  setPhotoQualityCheckFilter: (value: string[]) => void;
  componentErrorFilter: string;
  setComponentErrorFilter: (value: string) => void;
}

export function ActiveFilters({
  idFilter,
  setIdFilter,
  nameFilter,
  setNameFilter,
  orderFilter,
  setOrderFilter,
  captureAtFilterStart,
  captureAtFilterEnd,
  setCaptureAtFilterStart,
  setCaptureAtFilterEnd,
  classificationFilter,
  setClassificationFilter,
  confidenceFilter,
  setConfidenceFilter,
  captureCountFilter,
  setCaptureCountFilter,
  componentCountFilter,
  setComponentCountFilter,
  donenessFilter,
  setDonenessFilter,
  overallEvaluationStatusFilter,
  setOverallEvaluationStatusFilter,
  baseComponentsStatusFilter,
  setBaseComponentsStatusFilter,
  extrasStatusFilter,
  setExtrasStatusFilter,
  removalsStatusFilter,
  setRemovalsStatusFilter,
  pizzaSaverStatusFilter,
  setPizzaSaverStatusFilter,
  pizzaTypeFilter,
  setPizzaTypeFilter,
  pizzaTypes,
  onFilterClick,
  hasExtrasFilter,
  setHasExtrasFilter,
  hasRemovalsFilter,
  setHasRemovalsFilter,
  photoQualityStatusFilter,
  setPhotoQualityStatusFilter,
  photoQualityCheckFilter,
  setPhotoQualityCheckFilter,
  componentErrorFilter,
  setComponentErrorFilter,
}: ActiveFiltersProps) {
  const { t } = useLocalization();
  const photoQualityCheckOptions = getPhotoQualityCheckOptions();
  const photoQualityCheckLabels = photoQualityCheckFilter.map(
    (value) => photoQualityCheckOptions.find((option) => option.value === value)?.label ?? value,
  );
  /** display of the status filter values (OK / NOK / NONE) */
  const statusLabels = (values: string[]) =>
    values
      .map((value) =>
        value === "OK"
          ? t("status.ok")
          : value === "NOK"
            ? t("status.nok")
            : value === "NONE"
              ? t("pizzas.filters.statusNone")
              : value,
      )
      .join(", ");
  const formatDateRange = (start?: Date, end?: Date) => {
    if (!start && !end) return null;
    if (start && end) {
      return t("pizzas.activeFilters.dateRange", { from: formatDate(start), to: formatDate(end) });
    }
    if (start) return t("common.from", { date: formatDate(start) });
    if (end) return t("common.until", { date: formatDate(end) });
    return null;
  };

  const getPizzaTypeNames = (typeIds: string[]) => {
    if (!pizzaTypes || typeIds.length === 0) return [];
    return typeIds
      .map(id => pizzaTypes.find(type => type.id === id)?.name)
      .filter(Boolean) as string[];
  };

  const clearAllFilters = () => {
    setIdFilter('');
    setNameFilter('');
    setOrderFilter('');
    setCaptureAtFilterStart(undefined);
    setCaptureAtFilterEnd(undefined);
    setClassificationFilter('');
    setConfidenceFilter('');
    setCaptureCountFilter('');
    setComponentCountFilter('');
    setDonenessFilter('');
    setOverallEvaluationStatusFilter([]);
    setBaseComponentsStatusFilter([]);
    setExtrasStatusFilter([]);
    setRemovalsStatusFilter([]);
    setPizzaSaverStatusFilter([]);
    setPizzaTypeFilter([]);
    setHasExtrasFilter(false);
    setHasRemovalsFilter(false);
    setPhotoQualityStatusFilter([]);
    setPhotoQualityCheckFilter([]);
    setComponentErrorFilter('');
  };

  const activeFilters = [
    { label: t('pizzas.filters.pizzaIds'), value: idFilter, clear: () => setIdFilter('') },
    { label: t('pizzas.filters.pizzaName'), value: nameFilter, clear: () => setNameFilter('') },
    { label: t('pizzas.filters.orderIds'), value: orderFilter, clear: () => setOrderFilter('') },
    { label: t('pizzas.filters.captureDate'), value: formatDateRange(captureAtFilterStart, captureAtFilterEnd), clear: () => { setCaptureAtFilterStart(undefined); setCaptureAtFilterEnd(undefined); } },
    { label: t('pizzas.filters.detection'), value: classificationFilter ? (classificationFilter.toLowerCase() === 'no pizza' ? t('status.noPizza') : t('common.pizza')) : '', clear: () => setClassificationFilter('') },
    { label: t('pizzas.columns.confidence'), value: confidenceFilter, clear: () => setConfidenceFilter('') },
    { label: t('pizzas.filters.captureCount'), value: captureCountFilter, clear: () => setCaptureCountFilter('') },
    { label: t('pizzas.columns.componentCount'), value: componentCountFilter, clear: () => setComponentCountFilter('') },
    { label: t('pizzas.columns.doneness'), value: donenessFilter, clear: () => setDonenessFilter('') },
    { label: t('pizzas.columns.verdict'), value: statusLabels(overallEvaluationStatusFilter), clear: () => setOverallEvaluationStatusFilter([]) },
    { label: t('pizzas.columns.ingredients'), value: statusLabels(baseComponentsStatusFilter), clear: () => setBaseComponentsStatusFilter([]) },
    { label: t('pizzas.columns.extras'), value: statusLabels(extrasStatusFilter), clear: () => setExtrasStatusFilter([]) },
    { label: t('pizzas.columns.removals'), value: statusLabels(removalsStatusFilter), clear: () => setRemovalsStatusFilter([]) },
    { label: t('pizzas.columns.pizzaSaver'), value: statusLabels(pizzaSaverStatusFilter), clear: () => setPizzaSaverStatusFilter([]) },
    { label: t('pizzas.filters.pizzaTypes'), value: getPizzaTypeNames(pizzaTypeFilter).join(', '), clear: () => setPizzaTypeFilter([]) },
    { label: t('pizzas.filters.hasExtras'), value: hasExtrasFilter ? t('common.yes') : '', clear: () => setHasExtrasFilter(false) },
    { label: t('pizzas.filters.hasRemovals'), value: hasRemovalsFilter ? t('common.yes') : '', clear: () => setHasRemovalsFilter(false) },
    { label: t('pizzas.columns.photoQuality'), value: statusLabels(photoQualityStatusFilter), clear: () => setPhotoQualityStatusFilter([]) },
    { label: t('pizzas.filters.photoQualityCheck'), value: photoQualityCheckLabels.join(', '), clear: () => setPhotoQualityCheckFilter([]) },
    { label: t('pizzas.filters.componentError'), value: componentErrorFilter.trim(), clear: () => setComponentErrorFilter('') },
  ].filter(filter => filter.value);

  if (activeFilters.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        {t("pizzas.activeFilters.none")}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 py-1">
      <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
        {t("pizzas.activeFilters.title")}:
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {activeFilters.map((filter, index) => (
          <Badge 
            key={index} 
            variant="secondary" 
            className="flex items-center gap-1 pr-1 cursor-pointer hover:bg-secondary/80 hover:shadow-sm transition-all duration-150 select-none border border-border/50 hover:border-border"
            onClick={onFilterClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onFilterClick?.();
              }
            }}
            tabIndex={0}
            role="button"
            title={t("pizzas.activeFilters.edit", { filter: filter.label })}
          >
            <span className="font-medium text-xs">{filter.label}:</span>
            <span className="max-w-24 truncate text-xs" title={String(filter.value)}>
              {filter.value}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                filter.clear();
              }}
              title={t("pizzas.activeFilters.clear", { filter: filter.label })}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
        {activeFilters.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-xs h-6 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border/30 hover:border-destructive/30 transition-colors"
            title={t("pizzas.activeFilters.clearAll")}
          >
            {t("common.clearAll")}
          </Button>
        )}
      </div>
    </div>
  );
}
